/**
 * BannersManager — realtime list of `admin_banners`, newest-first. Filters by
 * severity / audience / enabled state, an inline enable toggle (no editor open
 * needed), and Edit / Delete row actions. Mirrors the realtime + row-action
 * idioms used elsewhere in the dashboard.
 */
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../common/Button';
import { BannerEditorModal } from './BannerEditorModal';
import {
  subscribeToBanners,
  setBannerEnabled,
  deleteBanner,
} from '../../services/firebase';
import type {
  AdminBanner,
  BannerAudience,
  BannerSeverity,
} from '../../types';

const SEVERITY_LABELS: Record<BannerSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  urgent: 'Urgent',
  promo: 'Promo',
};

const SEVERITY_BADGE: Record<BannerSeverity, string> = {
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-800',
  urgent: 'bg-red-100 text-red-800',
  promo: 'bg-brand-100 text-brand-700',
};

const AUDIENCE_LABELS: Record<BannerAudience, string> = {
  all: 'All users',
  free: 'Free',
  paid: 'Paid',
  never_paid: 'Never paid',
};

type SeverityFilter = 'all' | BannerSeverity;
type AudienceFilter = 'all' | BannerAudience;
type EnabledFilter = 'all' | 'enabled' | 'disabled';

/** Firestore Timestamp-ish → locale date-time, or '—' when absent. */
function fmtDateTime(value: unknown): string {
  if (!value) return '—';
  let date: Date | null = null;
  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    date = (value as { toDate: () => Date }).toDate();
  } else if (value instanceof Date) {
    date = value;
  }
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

/** Active-window summary for the list cell. */
function windowSummary(banner: AdminBanner): string {
  const start = fmtDateTime(banner.startsAt);
  const end = fmtDateTime(banner.endsAt);
  if (start === '—' && end === '—') return 'Always';
  return `${start} → ${end}`;
}

export function BannersManager() {
  const { user } = useAuth();
  const authorUid = user?.uid ?? '';

  const [banners, setBanners] = useState<AdminBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>('all');
  const [enabledFilter, setEnabledFilter] = useState<EnabledFilter>('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<AdminBanner | null>(null);
  // Ids with an in-flight toggle write, to disable the switch briefly.
  const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const unsubscribe = subscribeToBanners(
      (rows) => {
        setBanners(rows);
        setLoading(false);
      },
      () => {
        toast.error('Failed to load banners');
        setLoading(false);
      },
    );
    return unsubscribe;
  }, []);

  const filtered = useMemo(() => {
    return banners.filter((b) => {
      if (severityFilter !== 'all' && b.severity !== severityFilter) return false;
      if (audienceFilter !== 'all' && b.targetAudience !== audienceFilter)
        return false;
      if (enabledFilter === 'enabled' && !b.enabled) return false;
      if (enabledFilter === 'disabled' && b.enabled) return false;
      return true;
    });
  }, [banners, severityFilter, audienceFilter, enabledFilter]);

  const openNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (banner: AdminBanner) => {
    setEditing(banner);
    setEditorOpen(true);
  };

  const handleToggle = async (banner: AdminBanner) => {
    setTogglingIds((prev) => new Set(prev).add(banner.id));
    try {
      await setBannerEnabled(banner.id, !banner.enabled, authorUid);
    } catch (err) {
      console.error('Failed to toggle banner:', err);
      toast.error('Failed to update banner');
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(banner.id);
        return next;
      });
    }
  };

  const handleDelete = async (banner: AdminBanner) => {
    const label = (banner.title ?? '').trim() || 'this banner';
    if (!window.confirm(`Delete “${label}”? This can’t be undone.`)) return;
    try {
      await deleteBanner(banner.id);
      toast.success('Banner deleted');
    } catch (err) {
      console.error('Failed to delete banner:', err);
      toast.error('Failed to delete banner');
    }
  };

  const selectClass =
    'border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-gray-900">Banners</h2>
        <span className="text-sm text-gray-500">
          {banners.length} total
          {filtered.length !== banners.length && ` · ${filtered.length} shown`}
        </span>
        <div className="ml-auto">
          <Button variant="primary" size="sm" onClick={openNew}>
            + New banner
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-sm text-gray-600 flex items-center gap-1.5">
          Severity
          <select
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
            className={selectClass}
          >
            <option value="all">All</option>
            {(Object.keys(SEVERITY_LABELS) as BannerSeverity[]).map((s) => (
              <option key={s} value={s}>
                {SEVERITY_LABELS[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600 flex items-center gap-1.5">
          Audience
          <select
            value={audienceFilter}
            onChange={(e) => setAudienceFilter(e.target.value as AudienceFilter)}
            className={selectClass}
          >
            <option value="all">All</option>
            {(Object.keys(AUDIENCE_LABELS) as BannerAudience[]).map((a) => (
              <option key={a} value={a}>
                {AUDIENCE_LABELS[a]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-gray-600 flex items-center gap-1.5">
          State
          <select
            value={enabledFilter}
            onChange={(e) => setEnabledFilter(e.target.value as EnabledFilter)}
            className={selectClass}
          >
            <option value="all">All</option>
            <option value="enabled">Enabled</option>
            <option value="disabled">Disabled</option>
          </select>
        </label>
      </div>

      {loading ? (
        <div className="text-gray-500 italic py-8 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-gray-500 italic py-8 text-center border border-dashed border-gray-200 rounded-lg">
          {banners.length === 0
            ? 'No banners yet. Create one to get started.'
            : 'No banners match the current filters.'}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left font-medium px-3 py-2">Enabled</th>
                <th className="text-left font-medium px-3 py-2">Title</th>
                <th className="text-left font-medium px-3 py-2">Severity</th>
                <th className="text-left font-medium px-3 py-2">Audience</th>
                <th className="text-left font-medium px-3 py-2">Active window</th>
                <th className="text-right font-medium px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((banner) => (
                <tr key={banner.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={banner.enabled}
                      disabled={togglingIds.has(banner.id)}
                      onClick={() => handleToggle(banner)}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-50 ${
                        banner.enabled ? 'bg-brand-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          banner.enabled ? 'translate-x-4' : 'translate-x-0.5'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-800 max-w-xs">
                    <div className="truncate">
                      {(banner.title ?? '').trim() || (
                        <span className="text-gray-300">(untitled)</span>
                      )}
                    </div>
                    {banner.dismissible && (
                      <span className="text-[10px] text-gray-400">
                        dismissible
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${SEVERITY_BADGE[banner.severity]}`}
                    >
                      {SEVERITY_LABELS[banner.severity]}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {AUDIENCE_LABELS[banner.targetAudience]}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs whitespace-nowrap">
                    {windowSummary(banner)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEdit(banner)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDelete(banner)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BannerEditorModal
        isOpen={editorOpen}
        onClose={() => setEditorOpen(false)}
        banner={editing}
        authorUid={authorUid}
        onSaved={() => {
          // Realtime subscription refreshes the list; nothing to do here.
        }}
      />
    </div>
  );
}
